/**
 * Per-admin notification preferences (localStorage, keyed by admin id).
 */
import { isAdminChatOpen } from "./openAdminChatIds";

const STORAGE_PREFIX = "naseebi_admin_notify_prefs_";

export const DEFAULT_ADMIN_NOTIFY_PREFS = {
  enabled: true,
  browser: true,
  toast: true,
  types: {
    support_ticket: true,
    user_registered: true,
    verification_pending: true,
    chat_message: true,
    user_online: true,
  },
};

function cloneDefaults() {
  return {
    ...DEFAULT_ADMIN_NOTIFY_PREFS,
    types: { ...DEFAULT_ADMIN_NOTIFY_PREFS.types },
  };
}

function storageKey(adminId) {
  return `${STORAGE_PREFIX}${adminId || "anon"}`;
}

export function getPrefs(adminId) {
  try {
    const raw = localStorage.getItem(storageKey(adminId));
    if (!raw) return cloneDefaults();
    const parsed = JSON.parse(raw);
    return {
      enabled: parsed.enabled !== false,
      browser: parsed.browser !== false,
      toast: parsed.toast !== false,
      types: {
        ...DEFAULT_ADMIN_NOTIFY_PREFS.types,
        ...(parsed.types && typeof parsed.types === "object" ? parsed.types : {}),
      },
    };
  } catch {
    return cloneDefaults();
  }
}

export function setPrefs(adminId, patch = {}) {
  const current = getPrefs(adminId);
  const next = {
    enabled: patch.enabled != null ? !!patch.enabled : current.enabled,
    browser: patch.browser != null ? !!patch.browser : current.browser,
    toast: patch.toast != null ? !!patch.toast : current.toast,
    types: {
      ...current.types,
      ...(patch.types && typeof patch.types === "object" ? patch.types : {}),
    },
  };
  try {
    localStorage.setItem(storageKey(adminId), JSON.stringify(next));
  } catch {
    // ignore quota
  }
  return next;
}

/**
 * Show/ring only alerts that belong to the logged-in admin role.
 * CSR → outreach chats; Chat Support → tickets; leads/admin see both.
 */
export function isAlertForLoginRole(alert, roleDisplay) {
  const role = String(roleDisplay || "").trim().toLowerCase();
  const type = String(alert?.type || "").toLowerCase();
  const channel = String(alert?.kind || alert?.meta?.channel || "").toLowerCase();

  if (!role) return true;
  if (role === "admin" || role === "developer") return true;

  const isChatSupport = role === "chat support";
  const isCsr = role === "csr";
  const isCsrLead = role === "csr team lead";
  const isSales = role === "sales" || role === "sales team lead";

  if (type === "support_ticket") {
    return isChatSupport || isCsrLead;
  }

  if (type === "chat_message") {
    if (channel === "support" || channel === "chat_support") {
      return isChatSupport || isCsrLead;
    }
    if (channel === "csr") return isCsr || isCsrLead;
    if (channel === "sales") return isSales;
    if (isCsr) return false;
    if (isChatSupport) return channel === "support" || !channel;
    return true;
  }

  if (type === "user_online") {
    if (channel === "support" || channel === "chat_support") {
      return isChatSupport || isCsrLead;
    }
    if (channel === "csr") return isCsr || isCsrLead;
    if (isCsr) return false;
    if (isChatSupport) return false;
    return true;
  }

  if (type === "user_registered" || type === "verification_pending") {
    return isCsrLead || isSales;
  }

  return true;
}

export function isAlertAllowed(prefs, alertType) {
  if (!prefs || prefs.enabled === false) return false;
  if (!alertType) return true;
  const types = prefs.types || {};
  if (Object.prototype.hasOwnProperty.call(types, alertType)) {
    return types[alertType] !== false;
  }
  return true;
}

export function isChatAlreadyOpen(alert) {
  if (typeof window === "undefined") return false;
  const openTicketId = window.__naseebiOpenTicketId;
  const meta = alert?.meta || {};
  if (isAdminChatOpen(meta.chatId)) {
    return true;
  }
  if (
    openTicketId != null &&
    meta.ticketId != null &&
    String(openTicketId) === String(meta.ticketId)
  ) {
    return true;
  }
  if (
    openTicketId != null &&
    alert?.type === "chat_message" &&
    alert?.refId != null &&
    String(openTicketId) === String(alert.refId) &&
    (meta.channel === "support" || meta.ticketId != null)
  ) {
    return true;
  }
  return false;
}
